import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { DeviceAssignmentRow } from "@/features/devices/deviceAssignmentRows";
import {
  canRevokeFirstTrust,
  firstTrustLabel,
  firstTrustPhase,
  FirstTrustPurpose,
  isCredentialBound,
  isFirstTrustPlayerComplete,
  trustLifecycleLabel,
  trustLifecycleStatus,
} from "@/features/devices/firstTrust";
import {
  useListMasterAccountsQuery,
  useListMasterAccountLocationsQuery,
} from "@/features/accounts/accountsApi";
import { useGetLocationZonesQuery } from "@/features/devices/devicesApi";
import styles from "./firstTrustModals.module.scss";

export type FirstTrustOpenResult = {
  serialNumber: string;
  expiresAt: string;
  firstTrustStatus: string;
  firstTrustWindowId?: string;
  environment?: string;
  purpose?: string;
  assignmentIntent?: string | null;
};

export type ReEnrollAssignmentChoice = "KEEP_EXISTING" | "CHANGE_ASSIGNMENT";

export type ReEnrollConfirmPayload = {
  assignmentIntent: ReEnrollAssignmentChoice;
  masterAccountId?: string;
  locationId?: string;
  zoneId?: string;
};

type Props = {
  show: boolean;
  device: DeviceAssignmentRow | null;
  stageLabel: string;
  opening: boolean;
  revoking: boolean;
  error: string | null;
  authorized: FirstTrustOpenResult | null;
  purpose: FirstTrustPurpose;
  onClose: () => void;
  onConfirmOpen: (payload?: ReEnrollConfirmPayload) => void;
  onRevoke: () => void;
};

function remainingLabel(expiresAt: string, nowMs: number): string {
  const exp = Date.parse(expiresAt);
  if (!Number.isFinite(exp)) return expiresAt;
  const ms = exp - nowMs;
  if (ms <= 0) return "EXPIRED";
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${String(sec).padStart(2, "0")} remaining`;
}

const FirstTrustModal: React.FC<Props> = ({
  show,
  device,
  stageLabel,
  opening,
  revoking,
  error,
  authorized,
  purpose,
  onClose,
  onConfirmOpen,
  onRevoke,
}) => {
  const serial = device?.serialNumber || authorized?.serialNumber || "";
  const phase = device ? firstTrustPhase(device) : "ineligible";
  const lifecycle = device ? trustLifecycleStatus(device) : null;
  const isReEnroll = purpose === "re-enroll";
  const playerComplete = isFirstTrustPlayerComplete(device, authorized);
  // Revoke is ONLY for an open/unconsumed window — never after Player completion.
  const canRevoke = Boolean(device && canRevokeFirstTrust(device));
  const showAuthorized =
    !playerComplete && (Boolean(authorized) || phase === "awaiting");
  const expiresAt = authorized?.expiresAt || device?.firstTrustExpiresAt || "";
  const showFirstBindConfirm =
    !isReEnroll && phase === "ready" && !authorized && !playerComplete;
  const showReEnrollConfirm =
    isReEnroll &&
    !authorized &&
    !playerComplete &&
    (phase === "enrolled" ||
      phase === "expired" ||
      phase === "revoked" ||
      lifecycle === "re-enrollment-required") &&
    Boolean(device && isCredentialBound(device));
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [assignmentChoice, setAssignmentChoice] =
    useState<ReEnrollAssignmentChoice>("KEEP_EXISTING");
  const [masterAccountId, setMasterAccountId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [zoneId, setZoneId] = useState("");

  useEffect(() => {
    if (!show) return;
    setAssignmentChoice("KEEP_EXISTING");
    setMasterAccountId(device?.masterAccountId || "");
    setLocationId(device?.locationId || "");
    setZoneId(device?.zoneId || "");
  }, [show, device?.id, device?.masterAccountId, device?.locationId, device?.zoneId]);

  useEffect(() => {
    if (!show || !showAuthorized || !expiresAt) return undefined;
    setNowMs(Date.now());
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [show, showAuthorized, expiresAt]);

  const showChangeSelectors =
    showReEnrollConfirm && assignmentChoice === "CHANGE_ASSIGNMENT";

  const { data: accountsData, isFetching: loadingAccounts } =
    useListMasterAccountsQuery(
      { kind: "client", view: "active" },
      { skip: !showChangeSelectors },
    );
  const { data: locationsData, isFetching: loadingLocations } =
    useListMasterAccountLocationsQuery(masterAccountId, {
      skip: !showChangeSelectors || !masterAccountId,
    });
  const { data: zonesData, isFetching: loadingZones } = useGetLocationZonesQuery(
    locationId,
    { skip: !showChangeSelectors || !locationId },
  );

  const accounts = accountsData?.masterAccounts || [];
  const locations = locationsData?.locations || [];
  const zones = useMemo(
    () => (zonesData?.zones || []).filter((z) => !z.occupied),
    [zonesData],
  );

  const hasCurrentAssignment = Boolean(
    device?.assignedTo || device?.location || device?.zone,
  );

  const changeReady = Boolean(masterAccountId && locationId && zoneId);
  const reEnrollReady =
    assignmentChoice === "KEEP_EXISTING" ||
    (assignmentChoice === "CHANGE_ASSIGNMENT" && changeReady);

  const handleConfirm = () => {
    if (!isReEnroll) {
      onConfirmOpen();
      return;
    }
    if (assignmentChoice === "CHANGE_ASSIGNMENT") {
      onConfirmOpen({
        assignmentIntent: "CHANGE_ASSIGNMENT",
        masterAccountId,
        locationId,
        zoneId,
      });
      return;
    }
    onConfirmOpen({ assignmentIntent: "KEEP_EXISTING" });
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      dialogClassName={styles.firstTrustDialog}
      data-testid="first-trust-modal"
      data-purpose={purpose}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {isReEnroll ? "Re-enroll Player" : "Enroll Player"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {device && (
          <>
            <p className={styles.serialLine}>Player {serial}</p>
            <p>
              <strong>Environment:</strong> {stageLabel.toUpperCase()}
            </p>
            <p>
              <strong>Serial:</strong> {serial}
            </p>
            <p>
              <strong>Inventory:</strong>{" "}
              {(device.inventoryStatus || device.status || "—").toUpperCase()} /{" "}
              {(device.activationStatus || "PRE-ACTIVATION").toUpperCase()}
            </p>
            <p data-testid="first-trust-lifecycle-status">
              <strong>Status:</strong>{" "}
              {trustLifecycleLabel(lifecycle) || firstTrustLabel(phase)}
            </p>
            <p>
              <strong>Credential:</strong>{" "}
              {device.credentialBound || isCredentialBound(device)
                ? "BOUND"
                : "UNBOUND"}
            </p>
            {device.id ? (
              <p>
                <strong>Device ID:</strong> {device.id}
              </p>
            ) : null}
          </>
        )}

        {showFirstBindConfirm && (
          <>
            <p>Authorize first-time enrollment for this Player.</p>
            <Alert variant="warning" data-testid="first-trust-enroll-warning">
              This Player has not yet completed enrollment. Enrolling authorizes
              its device identity for KrossKast. This opens a 10-minute
              server-side window. No secret is generated. No USB or code is
              required.
            </Alert>
          </>
        )}

        {showReEnrollConfirm && (
          <>
            <p>Authorize re-enrollment for this previously enrolled Player.</p>
            <Alert variant="warning" data-testid="first-trust-reenroll-warning">
              Re-enrolling will authorize the new identity and revoke/supersede
              the previous device credential. Assignment is chosen explicitly
              below — it is never inferred from historical pointers alone.
            </Alert>

            <div
              className={styles.currentAssignmentBlock}
              data-testid="first-trust-current-assignment"
            >
              <h6>Current Assignment</h6>
              {hasCurrentAssignment ? (
                <>
                  <p>
                    <strong>Master Account:</strong>{" "}
                    {device?.assignedTo || "—"}
                  </p>
                  <p>
                    <strong>Location:</strong> {device?.location || "—"}
                  </p>
                  <p>
                    <strong>Zone:</strong> {device?.zone || "—"}
                  </p>
                </>
              ) : (
                <p data-testid="first-trust-no-assignment">
                  No active assignment on record for this Player.
                </p>
              )}
            </div>

            <p data-testid="first-trust-still-located">
              Is this Player still located at this assignment?
            </p>

            <Form data-testid="first-trust-assignment-choice">
              <Form.Check
                type="radio"
                id="reenroll-keep"
                name="assignmentChoice"
                label="Yes — Keep Current Assignment"
                checked={assignmentChoice === "KEEP_EXISTING"}
                onChange={() => setAssignmentChoice("KEEP_EXISTING")}
                data-testid="first-trust-keep-assignment"
              />
              <Form.Check
                type="radio"
                id="reenroll-change"
                name="assignmentChoice"
                label="No — Change Assignment"
                checked={assignmentChoice === "CHANGE_ASSIGNMENT"}
                onChange={() => setAssignmentChoice("CHANGE_ASSIGNMENT")}
                data-testid="first-trust-change-assignment"
              />
            </Form>

            {showChangeSelectors && (
              <div
                className={styles.changeSelectors}
                data-testid="first-trust-change-selectors"
              >
                <Form.Group className="mb-3">
                  <Form.Label>Master Account</Form.Label>
                  <Form.Select
                    value={masterAccountId}
                    disabled={loadingAccounts}
                    onChange={(e) => {
                      setMasterAccountId(e.target.value);
                      setLocationId("");
                      setZoneId("");
                    }}
                    data-testid="first-trust-select-ma"
                  >
                    <option value="">Select Master Account…</option>
                    {accounts.map((ma) => (
                      <option key={ma.masterAccountId} value={ma.masterAccountId}>
                        {ma.businessName || ma.masterAccountId}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Location</Form.Label>
                  <Form.Select
                    value={locationId}
                    disabled={!masterAccountId || loadingLocations}
                    onChange={(e) => {
                      setLocationId(e.target.value);
                      setZoneId("");
                    }}
                    data-testid="first-trust-select-location"
                  >
                    <option value="">Select Location…</option>
                    {locations.map((loc) => (
                      <option key={loc.locationId} value={loc.locationId}>
                        {loc.locationName || loc.locationId}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Zone</Form.Label>
                  <Form.Select
                    value={zoneId}
                    disabled={!locationId || loadingZones}
                    onChange={(e) => setZoneId(e.target.value)}
                    data-testid="first-trust-select-zone"
                  >
                    <option value="">Select Zone…</option>
                    {zones.map((z) => (
                      <option key={z.zoneId} value={z.zoneId}>
                        {z.name || z.zoneId}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Alert variant="secondary">
                  The Player will come online directly at the selected
                  destination. Historical assignment remains in audit history
                  only — it is never temporarily activated.
                </Alert>
              </div>
            )}
          </>
        )}

        {showAuthorized && (
          <Alert variant="success" data-testid="first-trust-authorized">
            <strong>
              {isReEnroll || authorized?.purpose === "re-enroll"
                ? "RE-ENROLLMENT AUTHORIZED"
                : "ENROLLMENT AUTHORIZED"}
            </strong>
            <div>
              {isReEnroll || authorized?.purpose === "re-enroll"
                ? `Player ${serial} may now complete secure re-enrollment with its new identity.`
                : `Player ${serial} may now complete secure first-time enrollment.`}
            </div>
            {authorized?.assignmentIntent && (
              <div data-testid="first-trust-authorized-intent">
                Assignment intent: {authorized.assignmentIntent}
              </div>
            )}
            {expiresAt && (
              <div data-testid="first-trust-expires">
                Expires: {remainingLabel(expiresAt, nowMs)} ({expiresAt})
              </div>
            )}
            <div data-testid="first-trust-awaiting-player">
              Status: Awaiting Player
            </div>
          </Alert>
        )}

        {playerComplete && (
          <Alert variant="success" data-testid="first-trust-complete">
            <strong>
              {isReEnroll || authorized?.purpose === "re-enroll"
                ? "RE-ENROLLMENT COMPLETE"
                : "ENROLLMENT COMPLETE"}
            </strong>
            <div>
              Player {serial} has a trusted device identity
              {device?.id ? ` (${device.id})` : ""}. Close this dialog to return
              to Device Assignments.
            </div>
            {authorized?.assignmentIntent && (
              <div data-testid="first-trust-complete-intent">
                Assignment intent: {authorized.assignmentIntent}
              </div>
            )}
          </Alert>
        )}

        {phase === "enrolled" && !isReEnroll && !authorized && !playerComplete && (
          <Alert variant="success" data-testid="first-trust-consumed">
            <strong>ENROLLED</strong>
            <div>
              Player {serial} has a trusted device identity. Device ID:{" "}
              {device?.id || "—"}.
            </div>
          </Alert>
        )}

        {phase === "expired" && !showReEnrollConfirm && !authorized && (
          <Alert variant="warning" data-testid="first-trust-expired">
            <strong>FIRST TRUST EXPIRED</strong>
          </Alert>
        )}

        {phase === "revoked" && !showReEnrollConfirm && !authorized && (
          <Alert variant="secondary" data-testid="first-trust-revoked">
            <strong>FIRST TRUST REVOKED</strong>
          </Alert>
        )}

        {error && (
          <Alert variant="danger" data-testid="first-trust-error">
            {error}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          type="button"
          onClick={onClose}
          data-testid="first-trust-close"
        >
          Close
        </Button>
        {canRevoke && (
          <Button
            variant="outline-danger"
            type="button"
            disabled={revoking}
            onClick={onRevoke}
            data-testid="first-trust-revoke"
          >
            {revoking ? "Revoking…" : "Revoke Authorization"}
          </Button>
        )}
        {showFirstBindConfirm && (
          <Button
            variant="primary"
            type="button"
            disabled={opening}
            onClick={handleConfirm}
            data-testid="first-trust-confirm"
          >
            {opening ? "Authorizing…" : "Enroll Player"}
          </Button>
        )}
        {showReEnrollConfirm && (
          <Button
            variant="primary"
            type="button"
            disabled={opening || !reEnrollReady}
            onClick={handleConfirm}
            data-testid="first-trust-reenroll-confirm"
          >
            {opening ? "Authorizing…" : "Re-enroll Player"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default FirstTrustModal;
